import { styled, css } from '@withjoy/joykit/theme';
import { animationTransition } from '../../../../utils/animationTransitions';
import type { SegmentedControllerVariant } from '../../SegmentedController.types';

const underlineVariant = css`
  height: 48px;
  padding: 0 16px;
  border-radius: 0;
  color: ${({ theme }) => theme.colors.mono8};
  flex: 0 0 auto;
`;

const pillVariant = css`
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.mono10};
  min-width: ${({ isMobile }: { isMobile?: boolean }) => (isMobile ? '152px' : 'initial')};
  flex: 1;
`;

/**
 * Rendered as a real `<button type="button">`. It used to be a `TextV2 as="a"` with no `href` and no
 * `tabIndex`, which made the whole control unreachable by keyboard (WCAG 2.1.1) -- so the reset
 * below undoes the UA button chrome the element brings with it.
 */
export const ToggleWrapper = styled.div<{ isMobile?: boolean; variant?: SegmentedControllerVariant; hasDescription?: boolean }>`
  appearance: none;
  background: none;
  border: none;
  margin: 0;
  font: inherit;

  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${animationTransition('color', { duration: '150ms' })};
  text-decoration: none;
  cursor: pointer;

  ${({ variant }) => (variant === 'underline' ? underlineVariant : pillVariant)}

  /*
   * The description line is optional content this item did not have to make room for before.
   * Without it, both variants use a fixed single-line height, so a second line would clip. Grow the
   * item only when a description is actually present -- an item with no description keeps its exact
   * original height and layout.
   */
  ${({ hasDescription, variant }) =>
    hasDescription &&
    css`
      height: auto;
      min-height: ${variant === 'underline' ? '48px' : '36px'};
      flex-direction: column;
      justify-content: center;
      gap: 2px;
      padding: 10px 8px;
      /*
       * A description turns a label into a two-line block, so the item has to give its text the
       * whole cell: no floor under its width. Each line stays one line and truncates (see
       * TruncatingHead / FixedTail below), so a long account name cannot push the row wider than
       * the container or make one item taller than its neighbour.
       */
      min-width: 0;
      text-align: center;
      border-radius: ${variant === 'underline' ? '0' : '9px'};
    `}

  &.active {
    color: ${({ theme }) => theme.colors.mono14};
  }

  &.disabled,
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  /*
   * Drawn *inside* the item. The items butt up against each other with no gap, and the pill variant
   * has only 4px of container padding around them, so a ring outside the item would overlap its
   * neighbours and reach the container's edge. Inset, it traces the item in both variants.
   * See doc/field-focus-consistency/PLAN.md.
   */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.inputBorderFocused};
    outline-offset: -2px;
  }
`;

/** The part of a description item's label that gives way: one line, cut with an ellipsis. */
export const TruncatingHead = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** The part that never gives way, e.g. an account's last four digits. */
export const FixedTail = styled.span`
  flex-shrink: 0;
  white-space: pre;
`;
